import React, { Fragment } from "react";
import classnames from "classnames";
import { observer } from "mobx-react";

import { IStreamData } from "@formant/core/model/IStreamData";
import { Timestamp } from "@formant/core/model/Timestamp";
import { Uuid } from "@formant/core/model/Uuid";

import { organizationStore } from "../../../stores";
import { Lazy } from "../../Lazy";
import { ModuleSize } from "../Module";
import { SpanSelector } from "../NumericModule/SpanSelector";
import * as styles from "./index.scss";
import { Value } from "./Value";

const Graph = Lazy(async () => (await import("../Graph")).Graph);

export interface INumericSetFullEntry {
    label: string;
    unit?: string;
    value?: number;
    toFixedDigits?: number;
    min?: number;
    max?: number;
    history: [Timestamp, number][];
}

export interface INumericSetProps {
    deviceId: Uuid;
    time: Date;
    live: boolean;
    startTime: Date;
    endTime: Date;
    entries: INumericSetFullEntry[];
    size?: ModuleSize;
    focused?: boolean;
    className?: string;
    invertedWarning?: boolean;
    old?: boolean;
    onSelectTime?: (time: Date) => void;
    span: number;
    onChangeSpan: (value: number) => void;
    showOnlyCurrentValues: boolean;
    configurationDisabled?: boolean;
    streamName?: string;
}

export const NumericSet = observer(function NumericSet(
    props: INumericSetProps,
) {
    const {
        deviceId,
        time,
        live,
        startTime,
        endTime,
        entries,
        size,
        focused,
        invertedWarning,
        old,
        onSelectTime,
        span,
        className,
        onChangeSpan,
        showOnlyCurrentValues,
        configurationDisabled,
    } = props;

    const defaultStreamThrottleHz = organizationStore.defaultStreamThrottleHz;

    return (
        <div
            className={classnames(
                className,
                styles["numeric-set"],
                size && styles[size],
                focused && styles.focused,
                showOnlyCurrentValues && styles.simple,
            )}
        >
            {entries.map((entry, i) => {
                const { label, unit, value, history, toFixedDigits } = entry;
                const min = entry.min || unit === "%" ? 0 : undefined;
                const max = entry.max || unit === "%" ? 100 : undefined;

                const data: IStreamData<"numeric"> = {
                    deviceId,
                    name: label,
                    type: "numeric",
                    tags: {},
                    points:
                        unit === "%"
                            ? history.map(([timestamp, x]) => [timestamp, x])
                            : history,
                };

                return (
                    <Fragment key={i}>
                        {!showOnlyCurrentValues && (
                            <div className={styles.history}>
                                <Graph
                                    defaultStreamThrottleHz={
                                        defaultStreamThrottleHz
                                    }
                                    time={time}
                                    live={live}
                                    startTime={startTime}
                                    endTime={endTime}
                                    min={min}
                                    max={max}
                                    data={[data]}
                                    splitByKey=""
                                    mode="numeric set"
                                    color={i}
                                    className={styles.graph}
                                    onSelectTime={onSelectTime}
                                />
                                {!configurationDisabled && (
                                    <div className={styles.configuration}>
                                        <SpanSelector
                                            value={span}
                                            onChange={onChangeSpan}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        <div className={styles.current}>
                            <Value
                                size={size}
                                value={value}
                                toFixedDigits={toFixedDigits}
                                unit={unit}
                                label={label}
                                invertedWarning={invertedWarning}
                                old={old}
                            />
                        </div>
                    </Fragment>
                );
            })}
        </div>
    );
});
